import './cellRenderers.css';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { ensureDisplayText } from '@icp/utils';
import { ConditionalPropertyPropType } from '../../../propTypes';
import LinkWrapper from '../../../wrappers/LinkWrapper';
import withCurrentData from './withCurrentData';

function MultiValueCellRenderer(props) {
  const {
    value,
    valueFormatted,
    separator,
    displayTextKeyPath,
    href,
    hrefSelector,
    hrefIsSiteBased,
    target,
    replace,
    suppressBasePath,
    suppressInheritIncludeDeleted,
  } = props;

  // '[object Object]' 是因为 ag-grid 在 valueFormatted 做了多余的事情，最后判断如果 value 是 array 会强制调用 value.join(', '),
  // 导致即使 colDef 没有提供 valueFormatter 的时候 [{}, {}] 会变成 '[object Object], [object Object]'
  const displayText =
    typeof valueFormatted === 'number' ||
    (typeof valueFormatted === 'string' && !valueFormatted.startsWith('[object Object]'))
      ? valueFormatted
      : MultiValueCellRenderer.toDisplayText(value, {
          separator,
          displayTextKeyPath,
        });

  if (href || hrefSelector) {
    return (
      <LinkWrapper
        className="table-multi-value-cell"
        componentProps={{
          href,
          hrefSelector,
          hrefIsSiteBased,
          target,
          replace,
          suppressBasePath,
          suppressInheritIncludeDeleted,
          suppressLinkColor: false,
        }}
      >
        {ensureDisplayText(displayText)}
      </LinkWrapper>
    );
  }

  return <span className="table-multi-value-cell">{ensureDisplayText(displayText)}</span>;
}

function getText(v, displayTextKeyPath, options) {
  const result = options?.find((item) => String(item?.value) === String(v?.value));
  return get(result || v, displayTextKeyPath);
}

MultiValueCellRenderer.toDisplayText = (value, cellRendererParams) => {
  const { separator = ', ', displayTextKeyPath = 'label', options } = cellRendererParams || {};

  // 兼容一下 SelectElement valueType 是 value 的情况
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  const text = []
    .concat(value)
    .filter(Boolean)
    .map((x) => (displayTextKeyPath ? getText(x, displayTextKeyPath, options) : x))
    .join(separator);

  // 表格导出现在已经没有调用 ag-grid 默认导出了。返回一个空白字符会让 copy cell 和导出 excel 都出现异常空格，所以注释掉这段代码
  /* if (text === '') {
    // 如果返回 undefined 或者 null，ag-grid 在它的源代码 valueService.ts 里会兜底让[{}] 会变成 '[object Object]'，
    // 如果返回 ''，没找到源代码在哪里，但是 clientSide 调用 ag-grid 自己的导出 excel 功能的时候，发现依然会 让[{}] 会变成 '[object Object]，
    // 测试下来返回一个空白字符是可以的，空白字符在 ui 上显示也不会有问题。
    if (Array.isArray(value) && value.length) {
      return ' ';
    }
    return undefined;
  } */

  return text;
};

MultiValueCellRenderer.keyCreator = (params) => {
  return []
    .concat(params.value)
    .filter(Boolean)
    .map((x) => x[params.context.multiValueKey])
    .join(',');
};

MultiValueCellRenderer.comparator = (valueA, valueB) => {
  // comparator 暂时拿不到 cellRendererParams
  return `${MultiValueCellRenderer.toDisplayText(valueA) ?? ''}`.localeCompare(
    `${MultiValueCellRenderer.toDisplayText(valueB) ?? ''}`,
  );
};

MultiValueCellRenderer.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])),
  ]),
  valueFormatted: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  data: PropTypes.shape({}),
  separator: PropTypes.string,
  displayTextKeyPath: PropTypes.string,
  // TODO, support pattern '/path/{id}'
  href: ConditionalPropertyPropType(PropTypes.string),
  hrefSelector: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      matched: ConditionalPropertyPropType(PropTypes.bool),
    }),
  ),
  /**
   * 如果值为 `false` 并且此链接位于 pbc 下，href 将自动拼接上 pbc 的 `token` 作为 `basename`，移动端将自动拼接上 `mobile` 的前缀
   */
  hrefIsSiteBased: PropTypes.bool,
  /**
   * 是否直接链接不通过 react router 的 base path
   */
  suppressBasePath: PropTypes.bool,
  /**
   * 是否禁止自动继承当前页面的 `include_deleted` 属性，此属性一般在当前页面 url 的 search params 里
   */
  suppressInheritIncludeDeleted: PropTypes.bool,
  /**
   * html a 标签的 target 属性
   */
  target: PropTypes.string,
  /**
   * react-router Link 的 replace 属性
   */
  replace: PropTypes.bool,
};

export default withCurrentData(MultiValueCellRenderer);
